import { useMemo } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text, Spinner } from "tamagui";
import { RiFireLine, RiWallet3Line } from "react-icons/ri";

import SkinV2Screen from "@components/layout/SkinV2Screen";
import PointsBalanceCard from "@components/points/PointsBalanceCard";
import { POINTS_TINT } from "@components/points/pointsTint";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import { colors } from "@styles/colors";
import { usePointsSummary, POINTS_KIND_LABEL, type PointsKind, type PointsTxn, POINTS_ENABLED } from "@utils/points";

import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import InfoNote from "./components/ui/InfoNote";

/** 網址上的種類（用連字號，與其他路由一致）→ 內部的 PointsKind。 */
const SLUG_TO_KIND: Record<string, PointsKind> = {
  "stored-gas": "stored_gas",
  account: "account",
};

/** 每種點數自己的文案。兩種點數是兩本帳，說明也各寫各的。 */
const COPY: Record<PointsKind, { tagline: string; note: string; empty: string }> = {
  stored_gas: {
    tagline: "退桶時沒用完的氣，1 點折 1 元",
    note: "退桶時桶內剩下的氣，秤重折算後存回這裡。下單時先套用優惠券，再折存氣點數 —— 只折得到瓦斯品項，不折商品、樓層費與服務費。",
    empty: "還沒有存氣紀錄",
  },
  account: {
    tagline: "溢繳或儲值的錢，1 點折 1 元",
    note: "月結溢繳或儲值留在瓦斯行的錢會存到這裡。下單時在優惠券與存氣點數之後折抵，整張訂單都能折。",
    empty: "還沒有異動紀錄",
  },
};

const fmtDateTime = (iso: string): string => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const TxnRow = ({ txn, isLast }: { txn: PointsTxn; isLast: boolean }) => {
  const income = txn.amount > 0;
  const t = POINTS_TINT[txn.kind];
  return (
    <XStack ai="center" gap="$3" px="$4" py="$3.5" bbw={isLast ? 0 : 1} bbc={colors.borderLight}>
      {/* 圖示色塊與 FnRow / ToggleRow 同一款（32、圓角 8、主色 15% 底） */}
      <View w={32} h={32} br={8} bg={t.tile} ai="center" jc="center" flexShrink={0}>
        {txn.kind === "stored_gas" ? <RiFireLine size={16} color={t.icon} /> : <RiWallet3Line size={16} color={t.icon} />}
      </View>
      <YStack f={1} miw={0}>
        <Text fontSize={14} fontWeight="700" color={colors.text}>
          {txn.title}
        </Text>
        <Text fontSize={11} color={colors.hint} mt={3}>
          {fmtDateTime(txn.created_at)}
          {txn.note ? ` · ${txn.note}` : ""}
        </Text>
      </YStack>
      <YStack ai="flex-end" flexShrink={0}>
        <Text fontSize={15} fontWeight="700" color={income ? colors.green : colors.sub} style={{ fontVariantNumeric: "tabular-nums" }}>
          {income ? "+" : "−"}
          {Math.abs(txn.amount).toLocaleString()}
        </Text>
        <Text fontSize={10} color={colors.hint} mt={2}>
          餘額 {txn.balanceAfter.toLocaleString()}
        </Text>
      </YStack>
    </XStack>
  );
};

/**
 * 單一種類的點數頁：`/account/detail/:contextId/points/:kind`
 * （`stored-gas` = 存氣點數、`account` = 帳戶點數）。
 *
 * 兩種點數是兩本帳（一個是氣、一個是錢，來源與可折範圍都不同），所以各給一頁、
 * 不做「一頁兩清單」或篩選 —— 從帳戶頁的餘額卡點進來就直接是那一本。
 * 兩者版面相同，故共用這支元件，由網址上的 kind 決定內容。
 *
 * TODO(points-api): 資料目前來自 @utils/points 的 mock；端點到位後只需換掉 fetchPointsSummary。
 */
export const PointsDetail = () => {
  const navigate = useNavigate();
  const { contextId, kind: kindSlug } = useParams();
  const kind = kindSlug ? SLUG_TO_KIND[kindSlug] : undefined;

  const { summary, balance, loading } = usePointsSummary(contextId);

  const txns = useMemo(() => (summary?.txns ?? []).filter((t) => t.kind === kind), [summary, kind]);

  // 網址被亂改（或舊的 /points 連結）、或點數功能關閉 → 回帳戶頁，別讓頁面空白
  if (!kind || !POINTS_ENABLED) return <Navigate to={`/account/detail/${contextId}`} replace />;

  const label = POINTS_KIND_LABEL[kind];
  const copy = COPY[kind];
  const points = kind === "stored_gas" ? balance.storedGas : balance.account;

  const subHeader = (
    <YStack gap="$1" pb="$2">
      <Text fontSize={22} fontWeight="800" color={v2.hazeInk}>
        {label}
      </Text>
      <Text fontSize={12} color={v2.hazeSub}>
        {copy.tagline}
      </Text>
    </YStack>
  );

  return (
    <SkinV2Screen title={label} onBack={() => navigate(-1)} subHeader={subHeader}>
      <YStack className="gas-stagger-kids" gap="$4">
        <PointsBalanceCard kind={kind} points={points} loading={loading} size="lg" />

        <YStack>
          <SectionLabel
            title="異動明細"
            right={
              !loading && txns.length > 0 ? (
                <Text fontSize={11} fontWeight="600" color={colors.sub}>
                  {txns.length} 筆
                </Text>
              ) : undefined
            }
          />
          <ListContainer>
            {loading ? (
              <YStack ai="center" py="$6">
                <Spinner size="small" color={colors.primary} />
              </YStack>
            ) : txns.length === 0 ? (
              <YStack ai="center" gap="$2" py="$6">
                <View w={44} h={44} br={12} bg={POINTS_TINT[kind].tile} ai="center" jc="center">
                  {kind === "stored_gas" ? (
                    <RiFireLine size={19} color={POINTS_TINT[kind].icon} />
                  ) : (
                    <RiWallet3Line size={19} color={POINTS_TINT[kind].icon} />
                  )}
                </View>
                <Text fontSize={13} color={v2.sub}>
                  {copy.empty}
                </Text>
              </YStack>
            ) : (
              txns.map((t, i) => <TxnRow key={t.id} txn={t} isLast={i === txns.length - 1} />)
            )}
          </ListContainer>
        </YStack>

        <InfoNote tone="primary">{copy.note}</InfoNote>
      </YStack>
    </SkinV2Screen>
  );
};

export default PointsDetail;
